import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header15 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template15' },
        { label: 'Shop', path: '/template15/shop' },
        { label: 'About', path: '/template15/about' },
        { label: 'Contact', path: '/template15/contact' },
        { label: 'Orders', path: '/template15/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&family=Nunito+Sans:wght@300;400;600&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(254, 252, 232, 0.97)' : '#FEFCE8',
                    borderBottom: scrolled ? '1px solid rgba(30, 64, 175, 0.1)' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    backdropFilter: scrolled ? 'blur(12px)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template15')}
                        style={{
                            fontFamily: "'Quicksand', sans-serif",
                            fontSize: '26px',
                            fontWeight: 700,
                            color: '#1E40AF',
                            cursor: 'pointer',
                            letterSpacing: '2px',
                            margin: 0,
                        }}
                    >
                        SAMUNDAR
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                        className="samundar-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Nunito Sans', sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#1E293B',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px 16px',
                                    borderRadius: '9999px',
                                    letterSpacing: '0.5px',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'rgba(30, 64, 175, 0.08)';
                                    e.target.style.color = '#1E40AF';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = '#1E293B';
                                }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Search
                            size={20}
                            style={{ color: '#1E293B', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#1E40AF')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1E293B')}
                        />
                        <User
                            size={20}
                            style={{ color: '#1E293B', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template15/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#1E40AF')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1E293B')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template15/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#1E293B', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#1E40AF')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1E293B')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#1E40AF',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'Nunito Sans', sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="samundar-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#1E293B' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FEFCE8',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#1E293B' }} />
                </button>

                <h2
                    style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: '24px',
                        fontWeight: 700,
                        color: '#1E40AF',
                        letterSpacing: '2px',
                        marginBottom: '40px',
                    }}
                >
                    SAMUNDAR
                </h2>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Quicksand', sans-serif",
                                fontSize: '22px',
                                fontWeight: 600,
                                color: '#1E293B',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '8px 24px',
                                borderRadius: '9999px',
                                transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'rgba(30, 64, 175, 0.08)';
                                e.target.style.color = '#1E40AF';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#1E293B';
                            }}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .samundar-desktop-nav {
                    display: flex !important;
                }
                .samundar-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .samundar-desktop-nav {
                        display: none !important;
                    }
                    .samundar-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header15;
